// useAgenda.ts
import { useContext, useState } from "react";
import api from "../../api";
import { useToast } from "../ToastContext";
import { LoadingContext } from "../LoadingContext";
import { AuthContext } from "../AuthContext";
import Swal from 'sweetalert2';

export interface AgendaType {
    id: number
    description: string;
    location: string;
    responsible: string;
    startDate: string;
    endDate: string;
    beca: string;
    step: number;
    cursoId?: number | null;
    cursoName?: string | null;
    cursoIds?: number[];
    cursoNames?: string[];
    tipoAgendaId: number;
    tipoAgendaName: string;
    empresaId: number;
    contratoId?: number | null;
    terceiros?: CreatedTerceiroServicoRequest[];
}

export interface CreateAgendaType {
    description: string;
    location: string;
    responsible: string;
    startDate: string;
    endDate: string;
    beca: string
    cursoId?: number | null;
    cursoIds?: number[];
    tipoAgendaId: number;
    empresaId: number;
    contratoId?: number | null;
    terceiros?: CreatedTerceiroServicoRequest[];
}

export interface EditAgendaType {
    id: number;
    description: string;
    location: string;
    responsible: string;
    startDate: string;
    endDate: string;
    cursoId?: number | null;
    cursoName?: string | null;
    cursoIds?: number[];
    cursoNames?: string[];
    beca: string
    step: number;
    tipoAgendaId: number;
    terceiros?: CreatedTerceiroServicoRequest[];
}

export interface ReallocateTerceiroPayload {
    fromAgendaId: number;
    toAgendaId: number;
    terceiroId: number;
}

export interface CreatedTerceiroServicoRequest {
    terceiroId: number;
    servicoId?: number | null;
    valorServico?: number | null;
}

export default function useAgenda() {
    const showToast = useToast();
    const { setLoading } = useContext(LoadingContext);
    const { handleLogout } = useContext(AuthContext);
    const [listAgenda, setListAgenda] = useState<AgendaType[]>([]);
    const [agendaPageCount, setAgendaPageCount] = useState<number>(0);
    const [initialListAgenda, setInitialListAgenda] = useState<AgendaType[]>([]);
    const [agenda, setAgenda] = useState<AgendaType>({
        id: 0,
        description: "",
        location: "",
        responsible: "",
        startDate: "",
        endDate: "",
        beca: "",
        step: 0,
        cursoId: 0,
        cursoName: "",
        tipoAgendaId: 0,
        tipoAgendaName: "",
        empresaId: 0,
        contratoId: null,
        terceiros: [],
    });

    function createAgenda(createAgendaType: CreateAgendaType): Promise<CreateAgendaType | null> {
        setLoading(true);

        const payload = {
            ...createAgendaType,
            terceiros: normalizeTerceiros(createAgendaType.terceiros),
        };

        return api
            .post(`/Agenda/create`, payload)
            .then((response) => {
                const createdAgenda: CreateAgendaType = response.data;
                showToast("success", "Agenda criada com sucesso.");
                setLoading(false);
                return createdAgenda;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast("error", "Sessão desconectada. Favor login novamente.", error);
                } else {
                    showToast("error", "Erro ao criar agenda. Verifique novamente mais tarde.", error);
                }
                setLoading(false);
                console.error("Erro ao criar agenda:", error);
                return null;
            });
    }

    function getAgendaPaged(
        pageNumber: number,
        EmpresaId?: number,
        pageSize?: number,
        ContratoId?: number,
    ): Promise<AgendaType[] | null> {
        setLoading(true);
        let url = `/Agenda/getAgendaPaged?pageNumber=${pageNumber}&pageSize=${pageSize}`;

        if (EmpresaId) {
            url += `&EmpresaId=${EmpresaId}`;
        }

        if (ContratoId) {
            url += `&ContratoId=${ContratoId}`;
        }

        return api
            .get(url)
            .then((response) => {
                setListAgenda(response.data.result);
                setInitialListAgenda(response.data.result);
                setAgendaPageCount(response.data.pageCount);
                setLoading(false);
                return response.data.result;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast("error", "Sessão desconectada. Favor login novamente.", error);
                } else {
                    showToast("error", "Erro ao carregar agenda. Verifique novamente mais tarde.", error);
                }
                setLoading(false);
                console.error("Erro ao carregar agenda:", error);
                return null;
            });
    }

    function updateAgenda(updateAgendaData: EditAgendaType): Promise<EditAgendaType | null> {
        setLoading(true);

        const payload = {
            ...updateAgendaData,
            terceiros: normalizeTerceiros(updateAgendaData.terceiros),
        };

        return api
            .put(`/Agenda/${updateAgendaData.id}`, payload)
            .then((response) => {
                showToast("success", "Agenda atualizada com sucesso.");
                setLoading(false);
                return response.data;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast("error", "Sessão desconectada. Favor login novamente.", error);
                } else {
                    showToast("error", "Erro ao atualizar agenda. Verifique novamente mais tarde.", error);
                }
                setLoading(false);
                console.error("Erro ao atualizar agenda:", error);
                return null;
            });
    }

    function deleteAgenda(agendaId: number): Promise<AgendaType | null> {
        setLoading(true);
        return api
            .delete(`/Agenda/${agendaId}`)
            .then(() => {
                setAgenda({
                    id: 0,
                    description: "",
                    location: "",
                    responsible: "",
                    startDate: "",
                    endDate: "",
                    beca: "",
                    step: 0,
                    cursoId: 0,
                    cursoName: "",
                    tipoAgendaId: 0,
                    tipoAgendaName: "",
                    empresaId: 0,
                    contratoId: null,
                    terceiros: [],
                });
                showToast("success", "Agenda deletada com sucesso.");
                setLoading(false);
                return null;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast("error", "Sessão desconectada. Favor login novamente.", error);
                } else {
                    showToast("error", "Erro ao deletar agenda. Verifique novamente mais tarde.", error);
                }
                setLoading(false);
                console.error("Erro ao deletar agenda:", error);
                return null;
            });
    }

    function handleGetAgendaById(agendaId: number): Promise<AgendaType | null> {
        setLoading(true);
        return api
            .get(`/Agenda/${agendaId}`)
            .then((response) => {
                const raw = response.data as any;

                const terceiros: CreatedTerceiroServicoRequest[] | undefined =
                    raw?.terceiros
                        ? normalizeTerceiros(raw.terceiros)
                        : raw?.terceiroId
                            ? normalizeTerceiros(raw.terceiroId as number[])
                            : [];

                const agenda: AgendaType = {
                    ...raw,
                    terceiros,
                };

                setAgenda(agenda);
                setLoading(false);
                return agenda;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast("error", "Sessão desconectada. Favor login novamente.", error);
                } else {
                    showToast("error", "Erro ao buscar agenda pelo ID. Verifique novamente mais tarde.", error);
                }
                setLoading(false);
                console.error("Erro ao buscar agenda pelo ID:", error);
                return null;
            });
    }

    function printPdfBlob(blob: Blob) {
        const url = window.URL.createObjectURL(blob);
        const iframe = document.createElement('iframe');
        iframe.style.position = 'fixed';
        iframe.style.right = '0';
        iframe.style.bottom = '0';
        iframe.style.width = '0';
        iframe.style.height = '0';
        iframe.style.border = '0';
        iframe.src = url;

        const cleanup = () => {
            try {
                if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
            } catch { }
            try {
                window.URL.revokeObjectURL(url);
            } catch { }
        };

        iframe.onload = () => {
            try {
                iframe.contentWindow?.focus();
                iframe.contentWindow?.print();
            } finally {
                window.setTimeout(cleanup, 1500);
            }
        };

        document.body.appendChild(iframe);
    }

    async function downloadAgendaPdfByDate(date: Date) {
        setLoading(true);
        try {
            const response = await api.get(`/Agenda/getAgendaPdfByDate`, {
                params: { date: date.toISOString() },
                responseType: 'blob',
            });

            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            const formattedDate = date.toISOString().split('T')[0].split('-').reverse().join('_');
            a.download = `Agenda_${formattedDate}.pdf`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);

            showToast("success", "PDF da agenda baixado com sucesso!");
        } catch (error) {
            showToast("error", "Erro ao baixar o PDF da agenda.");
            console.error("Erro ao baixar o PDF da agenda:", error);
        } finally {
            setLoading(false);
        }
    }

    async function printAgendaPdfByDate(date: Date) {
        setLoading(true);
        try {
            const response = await api.get(`/Agenda/getAgendaPdfByDate`, {
                params: { date: date.toISOString() },
                responseType: 'blob',
            });

            const blob = new Blob([response.data], { type: 'application/pdf' });
            printPdfBlob(blob);
            showToast("success", "Relatório pronto para impressão!");
        } catch (error) {
            showToast("error", "Erro ao preparar impressão do PDF da agenda.");
            console.error("Erro ao preparar impressão do PDF da agenda:", error);
        } finally {
            setLoading(false);
        }
    }

    async function downloadAgendaPdfCustom(filtros: {
        empresaId: number;
        startDate?: string;
        endDate?: string;
        agendaIds?: number[];
        contratoId?: number;
    }) {
        try {
            const response = await api.post(`/Agenda/getAgendaPdfCustom`, filtros, {
                responseType: 'blob',
            });

            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Agenda_Personalizada.pdf`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
            showToast("success", "PDF gerado com sucesso!");
        } catch (error) {
            showToast("error", "Erro ao gerar PDF personalizado.");
            console.error(error);
        }
    }

    async function printAgendaPdfCustom(filtros: {
        empresaId: number;
        startDate?: string;
        endDate?: string;
        agendaIds?: number[];
        contratoId?: number;
    }) {
        setLoading(true);
        try {
            const response = await api.post(`/Agenda/getAgendaPdfCustom`, filtros, {
                responseType: 'blob',
            });

            const blob = new Blob([response.data], { type: 'application/pdf' });
            printPdfBlob(blob);
            showToast("success", "Relatório pronto para impressão!");
        } catch (error) {
            showToast("error", "Erro ao preparar impressão do PDF personalizado.");
            console.error(error);
        } finally {
            setLoading(false);
        }
    }

    async function downloadAgendaListPdf(filtros: {
        empresaId: number;
        startDate?: string;
        endDate?: string;
        agendaIds?: number[];
        contratoId?: number;
    }) {
        try {
            const response = await api.post(`/Agenda/getAgendaListPdf`, filtros, {
                responseType: 'blob',
            });

            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Agenda_Lista.pdf`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
            showToast("success", "PDF gerado com sucesso!");
        } catch (error) {
            showToast("error", "Erro ao gerar PDF da lista de eventos.");
            console.error(error);
        }
    }

    async function printAgendaListPdf(filtros: {
        empresaId: number;
        startDate?: string;
        endDate?: string;
        agendaIds?: number[];
        contratoId?: number;
    }) {
        setLoading(true);
        try {
            const response = await api.post(`/Agenda/getAgendaListPdf`, filtros, {
                responseType: 'blob',
            });

            const blob = new Blob([response.data], { type: 'application/pdf' });
            printPdfBlob(blob);
            showToast("success", "Relatório pronto para impressão!");
        } catch (error) {
            showToast("error", "Erro ao preparar impressão da lista de eventos.");
            console.error(error);
        } finally {
            setLoading(false);
        }
    }

    function listAllAgenda(empresaId: number, contratoId?: number): Promise<AgendaType[] | null> {
        setLoading(true);
        const queryParams = new URLSearchParams({ empresaId: empresaId.toString() });
        if (contratoId) {
            queryParams.append("contratoId", contratoId.toString());
        }

        return api
            .get(`/Agenda/list-all?${queryParams.toString()}`)
            .then((response) => {
                setListAgenda(response.data);
                setInitialListAgenda(response.data);
                setLoading(false);
                return response.data;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast("error", "Sessão desconectada. Favor login novamente.", error);
                } else {
                    showToast("error", "Erro ao carregar agenda completa.", error);
                }
                setLoading(false);
                console.error("Erro ao carregar agenda completa:", error);
                return null;
            });
    }

    async function realocarFotografo(payload: ReallocateTerceiroPayload): Promise<boolean> {
        setLoading(true);
        try {
            const { fromAgendaId, toAgendaId, terceiroId } = payload;

            if (!fromAgendaId || !toAgendaId || !terceiroId) {
                showToast("error", "Parâmetros inválidos para realocar o fotógrafo.");
                return false;
            }
            if (fromAgendaId === toAgendaId) {
                showToast("error", "Evento de origem e destino devem ser diferentes.");
                return false;
            }

            await api.post(`/Agenda/realocar-fotografo`, payload);
            showToast("success", "Fotógrafo realocado com sucesso.");
            return true;
        } catch (error: any) {
            if (error?.response?.status === 401) {
                handleLogout();
                showToast("error", "Sessão desconectada. Favor login novamente.", error);
            } else {
                const msg = error?.response?.data?.message || "Erro ao realocar fotógrafo. Tente novamente mais tarde.";
                showToast("error", msg, error);
            }
            return false;
        } finally {
            setLoading(false);
        }
    }

    function normalizeTerceiros(input?: CreatedTerceiroServicoRequest[] | number[] | null): CreatedTerceiroServicoRequest[] | undefined {
        if (!input) return undefined;

        if (Array.isArray(input) && typeof (input as any[])[0] === "number") {
            return (input as number[]).map(id => ({ terceiroId: id, servicoId: null, valorServico: null }));
        }

        return (input as CreatedTerceiroServicoRequest[])
            .filter(x => x && typeof x.terceiroId === "number")
            .map(x => ({ terceiroId: x.terceiroId, servicoId: x.servicoId ?? null, valorServico: typeof x.valorServico === "number" ? x.valorServico : null }));
    }

    async function checkConflitoHorarioFotografo(terceiroId: number, startDate: string, endDate: string): Promise<boolean> {
        try {
            const response = await api.get('/Agenda/terceiro-conflito', {
                params: {
                    terceiroId,
                    startDate,
                    endDate
                }
            });

            const conflito = response.data?.conflito === true;

            if (conflito) {
                await Swal.fire({
                    icon: 'warning',
                    title: 'Conflito de agenda',
                    text: 'Este fotógrafo já está alocado em outro evento neste horário.',
                    confirmButtonText: 'Ok'
                });
            }

            return conflito;
        } catch (error) {
            console.error('Erro ao verificar conflito de agenda:', error);
            return false;
        }
    }

    function getAgendasComRejeicao(params: {
        pageNumber: number;
        pageSize?: number;
        empresaId?: number;
        startDate?: string;
        endDate?: string;
    }): Promise<AgendaType[] | null> {
        setLoading(true);

        const qs = new URLSearchParams();
        qs.append("pageNumber", String(params.pageNumber || 1));
        qs.append("pageSize", String(params.pageSize || 12));
        if (params.empresaId) qs.append("empresaId", String(params.empresaId));
        if (params.startDate) qs.append("startDate", params.startDate);
        if (params.endDate) qs.append("endDate", params.endDate);

        return api
            .get(`/Agenda/com-rejeicao?${qs.toString()}`)
            .then((response) => {
                const data = response.data?.result as AgendaType[] || [];
                const pageCount = response.data?.pageCount ?? 0;
                setListAgenda(data);
                setInitialListAgenda(data);
                setAgendaPageCount(pageCount);
                setLoading(false);
                return data;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast("error", "Sessão desconectada. Favor login novamente.", error);
                } else {
                    const msg = error?.response?.data?.message || "Erro ao carregar agendas rejeitadas.";
                    showToast("error", msg, error);
                }
                setLoading(false);
                console.error("Erro ao carregar agendas rejeitadas:", error);
                return null;
            });
    }

    async function replaceAgendaCursos(agendaId: number, cursoIds: number[]): Promise<boolean> {
        setLoading(true);
        try {
            await api.put(`/Agenda/${agendaId}/cursos`, { cursoIds });
            showToast("success", "Cursos atualizados na agenda.");
            return true;
        } catch (error: any) {
            if (error?.response?.status === 401) {
                handleLogout();
                showToast("error", "Sessão desconectada. Favor login novamente.", error);
            } else {
                const msg = error?.response?.data?.message || "Erro ao atualizar cursos da agenda.";
                showToast("error", msg, error);
            }
            return false;
        } finally {
            setLoading(false);
        }
    }

    async function addCursoNaAgenda(agendaId: number, cursoId: number): Promise<boolean> {
        setLoading(true);
        try {
            await api.post(`/Agenda/${agendaId}/cursos/${cursoId}`);
            showToast("success", "Curso vinculado à agenda.");
            return true;
        } catch (error: any) {
            if (error?.response?.status === 401) {
                handleLogout();
                showToast("error", "Sessão desconectada. Favor login novamente.", error);
            } else {
                const msg = error?.response?.data?.message || "Erro ao vincular curso.";
                showToast("error", msg, error);
            }
            return false;
        } finally {
            setLoading(false);
        }
    }

    async function removeCursoDaAgenda(agendaId: number, cursoId: number): Promise<boolean> {
        setLoading(true);
        try {
            await api.delete(`/Agenda/${agendaId}/cursos/${cursoId}`);
            showToast("success", "Curso desvinculado da agenda.");
            return true;
        } catch (error: any) {
            if (error?.response?.status === 401) {
                handleLogout();
                showToast("error", "Sessão desconectada. Favor login novamente.", error);
            } else {
                const msg = error?.response?.data?.message || "Erro ao desvincular curso.";
                showToast("error", msg, error);
            }
            return false;
        } finally {
            setLoading(false);
        }
    }

    return {
        createAgenda,
        getAgendaPaged,
        updateAgenda,
        deleteAgenda,
        handleGetAgendaById,
        setListAgenda,
        listAgenda,
        setAgendaPageCount,
        agendaPageCount,
        setInitialListAgenda,
        initialListAgenda,
        setAgenda,
        agenda,
        downloadAgendaPdfByDate,
        downloadAgendaPdfCustom,
        printAgendaPdfByDate,
        printAgendaPdfCustom,
        downloadAgendaListPdf,
        printAgendaListPdf,
        listAllAgenda,
        realocarFotografo,
        checkConflitoHorarioFotografo,
        getAgendasComRejeicao,
        replaceAgendaCursos,
        addCursoNaAgenda,
        removeCursoDaAgenda,
    };
}
